
import React, { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import useFetch from "../../../components/useFetch";
import Prediction from "../../../components/modal/NavPanes/Prediction";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faClose,
  faMinimize,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import ErrorScreen from "../../../components/error/ErrorScreen";
import Loader from "../../../components/Loader";
import useFetchStatic from "../../../components/useFetchStatic";
import ModalComponent2 from "../../../components/modal/ModalComponent2";
import ModalComponent from "../../../components/modal/ModalComponent";
import "./Models.scss";
import { StyledTooltip } from "../label/annotation/EditorView/EditorTopNavigationBar/EditorTopNavigationBar";
import { EventSourcePolyfill } from "event-source-polyfill";
import { getCokkie } from "../../login/Login";
import getDataFromApi from "../../../components/getDataFromApi";
import { initSampleWorker, terminateSampleWorker } from "../../../worker/sampleSSEWorkerManager";
import { Skeleton } from "@mui/material";
import EvaluationVideo from "./EvaluationVideo";
import { ImagesIcon, QueueChipIcon } from "../../Common/SvgIcons/WorkflowIcons";
import { ImageCanvas } from "../../../components/modal/NavPanes/CanvaImage";

const PAGE_SIZE = 49;
const DESKTOP_PREVIEW_COLUMNS = 7;
const SEARCH_FILTER_KEYS = {
  class: "class",
  image_name: "image_name",
};

const normalizePredictionType = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const isFaceRecognitionType = (value) =>
  normalizePredictionType(value) === "facerecognition";

const getPreviewImageSrc = (value) => {
  if (!value) return "";

  const source = String(value).trim();
  if (
    source.startsWith("http://") ||
    source.startsWith("https://") ||
    source.startsWith("data:") ||
    source.startsWith("blob:")
  ) {
    return source;
  }

  return `data:image/jpeg;base64,${source}`;
};

const getAnnotationImage = (annotation) =>
  annotation?.image_url ||
  annotation?.video_stream ||
  annotation?.image ||
  annotation?.reference_img_url ||
  annotation?.annotation?.reference_img_url ||
  "";

const getVideoUrl = (frame) =>
  frame?.video_stream || frame?.video_url || frame?.image_url || "";

const getVideoId = (frame) => {
  if (frame?.video_name) return frame.video_name;
  if (String(frame?.media_name || "").match(/\.(mp4|webm|mov|m4v)$/i)) {
    return frame.media_name;
  }

  const url = getVideoUrl(frame);
  if (!url) return "video";

  try {
    return decodeURIComponent(new URL(url).pathname.split("/").pop()) || "video";
  } catch {
    return "video";
  }
};

const getFrameIndex = (key, frame) => {
  const explicit = Number(frame?.frame_index);
  if (Number.isInteger(explicit) && explicit >= 0) return explicit;

  const match = String(frame?.image_name || key || "").match(/frame[_-]?(\d+)/i);
  return match ? Number.parseInt(match[1], 10) : null;
};

const getPredictionInterval = (frame, fallbackInterval) => {
  const rawInterval = Number(
    frame?.prediction_interval ?? fallbackInterval
  );

  return Number.isFinite(rawInterval) && rawInterval > 0
    ? Math.floor(rawInterval)
    : 1;
};

const getPredictionSourceFrameIndex = (key, frame, fallbackInterval) => {
  const predictionIndex = getFrameIndex(key, frame);
  if (predictionIndex === null) return null;

  return predictionIndex * getPredictionInterval(frame, fallbackInterval);
};

const getFrameBoxes = (frame) => {
  const annotations = Array.isArray(frame?.annotation?.[0])
    ? frame.annotation[0]
    : [];

  return annotations.filter(Array.isArray).map((annotation, index) => {
    const score = Number(frame?.score?.[index]);
    const scoreText = Number.isFinite(score) ? ` (${score.toFixed(2)})` : "";

    return {
      x: Number(annotation[0]),
      y: Number(annotation[1]),
      w: Number(annotation[2]),
      h: Number(annotation[3]),
      label: `${annotation[4] ?? ""}${scoreText}`,
      color: annotation[5] || "lime",
    };
  });
};

const getVideoEntries = (payload) =>
  Object.entries(payload || {}).filter(([, frame]) =>
    frame &&
    typeof frame === "object" &&
    (frame.media_type === "video" || frame.video_stream || frame.video_url)
  );

const mergeVideoMedia = (media, entries) => {
  const next = { ...(media || {}) };
  const pendingVideos = new Map();

  entries.forEach(([key, frame]) => {
    const videoId = getVideoId(frame);
    let pending = pendingVideos.get(videoId);

    if (!pending) {
      const current = next[videoId] || {
        type: "video",
        frames: {},
        landmarksFrames: {},
      };
      pending = {
        current,
        // These maps can contain thousands of entries. They are append-only
        // while a result is streaming, so keep their identity instead of
        // copying the complete maps for every SSE message.
        frames: current.frames || {},
        landmarksFrames: current.landmarksFrames || {},
        url: current.url,
        videoFps: current.video_fps,
        predictionInterval: current.prediction_interval,
      };
      pendingVideos.set(videoId, pending);
    }

    // The stream index is the prediction result index. Convert it to the
    // corresponding source-video frame using prediction_interval.
    const frameIndex = getPredictionSourceFrameIndex(
      key,
      frame,
      pending.predictionInterval
    );

    if (frameIndex !== null) {
      const boxes = getFrameBoxes(frame);
      const landmarks = frame?.landmarks || [];

      // Keep one sparse entry per prediction window. EvaluationVideo resolves
      // the intervening source frames to this entry during playback.
      pending.frames[frameIndex] = boxes;
      pending.landmarksFrames[frameIndex] = landmarks;
    }

    // The backend can repeat the (occasionally very large) video source on
    // every prediction. Retain the first source rather than replacing it for
    // every frame and creating needless decoder/source churn.
    pending.url = pending.url || getVideoUrl(frame);
    pending.videoFps = frame?.video_fps ?? pending.videoFps;
    pending.predictionInterval =
      frame?.prediction_interval ?? pending.predictionInterval;
  });

  pendingVideos.forEach((pending, videoId) => {
    next[videoId] = {
      ...pending.current,
      type: "video",
      media_name: videoId,
      url: pending.url,
      frames: pending.frames,
      landmarksFrames: pending.landmarksFrames,
      video_fps: pending.videoFps,
      prediction_interval: pending.predictionInterval,
    };
  });

  return next;
};

const mergeVideoAnnotations = (annotations, entries) => {
  const next = { ...(annotations || {}) };
  const latestFrames = new Map();

  entries.forEach(([, frame]) => {
    latestFrames.set(getVideoId(frame), frame);
  });

  latestFrames.forEach((frame, videoId) => {
    const current = next[videoId] || {};
    const currentMetadata = { ...current };
    const frameMetadata = { ...frame };

    [currentMetadata, frameMetadata].forEach((metadata) => {
      delete metadata.video_stream;
      delete metadata.video_url;
      delete metadata.image_url;
      delete metadata.reference_img_url;
    });

    next[videoId] = {
      ...currentMetadata,
      ...frameMetadata,
      media_type: "video",
      media_name: videoId,
    };
  });

  return next;
};

const collectLabelValues = (value, labels) => {
  if (value == null) return labels;

  if (typeof value === "string" || typeof value === "number") {
    labels.push(String(value));
    return labels;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectLabelValues(item, labels));
    return labels;
  }

  if (typeof value === "object") {
    Object.values(value).forEach((item) => collectLabelValues(item, labels));
  }

  return labels;
};

const collectPredictionLabels = (item) => {
  if (item == null || typeof item !== "object") return [];

  const labelSources = [
    item.label,
    item.class,
    item.class_name,
    item.name,
    item.annotation,
    item.annotations,
    item.predictions,
    item.detections,
    item.boxes,
  ];

  return labelSources.reduce(
    (labels, source) => collectLabelValues(source, labels),
    []
  );
};

const Samplepredictionstream = (props) => {
  const [images, setImages] = useState([]);
  const [predictions, setPredictions] = useState(false);
  // const [imagesData, setImagesData] = useState({ annotations: {} });
  const [imagesData, setImagesData] = useState(() => ({
    annotations: [],   // always an array
  }));
  const [selectedImage, setSelectedImage] = useState(null);
  const [loader, setLoader] = useState(true);
  const [sliderIndex, setSliderIndex] = useState();
  const [modalIsOpen, setModalIsOpen] = useState();
  const [Component, setComponent] = useState();
  const [ParentData, setParentData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);

  const sampleSSEWorker = new URL('../../../worker/sampleSSEWorker.js', import.meta.url);
  const workerRef = useRef(null);
  const videoEventSourceRef = useRef(null);
  const pendingVideoEntriesRef = useRef([]);
  const videoPayloadFlushRef = useRef(null);
  const hasStartedWorker = useRef(false);
  const previousEvaluationStatusRef = useRef(
    String(props.message || "").toLowerCase()
  );
  const previousLlmReportAvailableRef = useRef(
    props.llmReportAvailable === true
  );
  const scrollLockRef = useRef(false);
  const scrollContainerRef = useRef(null);
  const [status, setStatus] = useState('');
  const [loaderMessage, setLoaderMessage] = useState('Analysing..');
  const [media, setMedia] = useState({});
  const [annotationIndexMap, setAnnotationIndexMap] = useState({});
  const [conversionProgress, setConversionProgress] = useState(0); // Numeric progress value (e.g., 20, 50, 100)
  const [isConverting, setIsConverting] = useState(false); // Whether we’re currently in converting state
  const [elapsedTime, setElapsedTime] = useState(""); // 🕒 new state
  const [startTimeMap, setStartTimeMap] = useState({}); // { [id]: startTimestamp }
  const timerRefs = useRef({}); // { [id]: intervalId }
  const [evaluatingStep, setEvaluatingStep] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [pageInput, setPageInput] = useState();
  const [availablePages, setAvailablePages] = useState(1); // unlocked pages during Evaluating
  const [previewSearchValue, setPreviewSearchValue] = useState("");
  const [previewColumnLimit, setPreviewColumnLimit] = useState(() => {
    if (typeof window === "undefined") return DESKTOP_PREVIEW_COLUMNS;
    return window.matchMedia("(max-width: 768px)").matches
      ? 2
      : DESKTOP_PREVIEW_COLUMNS;
  });


  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const syncPreviewColumns = () => {
      setPreviewColumnLimit(
        mediaQuery.matches ? 2 : DESKTOP_PREVIEW_COLUMNS
      );
    };

    syncPreviewColumns();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", syncPreviewColumns);
      return () => mediaQuery.removeEventListener("change", syncPreviewColumns);
    }

    mediaQuery.addListener(syncPreviewColumns);
    return () => mediaQuery.removeListener(syncPreviewColumns);
  }, []);


  useEffect(() => {
    if (props.message !== "Evaluating") {
      setEvaluatingStep(0);
      return;
    }

    const t1 = setTimeout(() => {
      setEvaluatingStep(1);
    }, 5000);

    const t2 = setTimeout(() => {
      setEvaluatingStep(2);
    }, 8000);

    const t3 = setTimeout(() => {
      setEvaluatingStep(3);
    }, 10000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [props.message]);


  const displayedAnnotations = useMemo(() => {
    const annotations = imagesData.annotations || {};
    const searchValue = previewSearchValue.trim().toLowerCase();

    if (!searchValue) return annotations;

    const searchTerms = searchValue
      .split(",")
      .map((term) => term.trim())
      .filter(Boolean);

    return Object.fromEntries(
      Object.entries(annotations).filter(([annotationKey, item]) => {
        const imageNames = [
          annotationKey,
          item?.image_name,
          item?.media_name,
        ];

        const matchesImageName = searchTerms.some((searchTerm) =>
          imageNames.some((name) =>
            String(name || "").toLowerCase().includes(searchTerm)
          )
        );

        if (matchesImageName) return true;

        const predictionLabels = collectPredictionLabels(item).map((label) =>
          label.toLowerCase()
        );

        return searchTerms.some((searchLabel) =>
          predictionLabels.some((label) => label.includes(searchLabel))
        );
      })
    );
  }, [imagesData.annotations, previewSearchValue]);

  const isFaceRecognitionTask =
    isFaceRecognitionType(props.task_type) ||
    Object.values(displayedAnnotations).some((item) =>
      isFaceRecognitionType(item?.type || item?.task_type || item?.taskType)
    );

  const filteredImages = isFaceRecognitionTask
    ? []
    : Object.values(displayedAnnotations).filter(
        (item) =>
          !isFaceRecognitionType(item?.type) && item?.media_type !== "video"
      );

  const displayedMedia = useMemo(() => {
    if (!previewSearchValue.trim()) return media;

    const visibleEntries = Object.entries(displayedAnnotations);
    return Object.fromEntries(
      Object.entries(media).filter(([mediaName]) =>
        visibleEntries.some(
          ([annotationKey, item]) =>
            annotationKey.startsWith(mediaName) || item?.media_name === mediaName
        )
      )
    );
  }, [previewSearchValue, displayedAnnotations, media]);

  const displayedMediaEntries = useMemo(
    () => Object.entries(displayedMedia),
    [displayedMedia]
  );
  const streamedVideoCount = displayedMediaEntries.filter(
    ([, item]) => item?.type === "video"
  ).length;
  const hasStreamedVideo = streamedVideoCount > 0;
  const hasPreviewContent =
    images.length > 0 || displayedMediaEntries.length > 0;

  const getPreviewGridTemplateColumns = useCallback(
    () => `repeat(${previewColumnLimit}, minmax(0, 1fr))`,
    [previewColumnLimit]
  );




  const handleViewMore = () => {
    if (!isLoadingNextPage && hasMorePages) {
      if (isStaticEvaluation) {
        fetchPage(page + 1);

      } else if (props.message === "Evaluating") {
        setImagesData({ annotations: [] });
        startWorker(page + 1);
        setPage(page + 1);
      }
    }
  };




  // console.log('media', Object.entries(media)?.length)



  // console.log('media', media)

  // ---- CORS / network error detection helper ----
  const isCorsLikeError = (errOrMsg) => {
    const msg = (typeof errOrMsg === "string" ? errOrMsg : errOrMsg?.message || "")
      .toLowerCase();
    return (
      msg.includes("cors") ||
      msg.includes("blocked by cors policy") ||
      msg.includes("failed to fetch") ||
      msg.includes("networkerror") ||
      msg.includes("typeerror: failed to fetch")
    );
  };

  const MAX_RETRIES = 3;

  const applyVideoPayload = useCallback((payload) => {
    const entries = getVideoEntries(payload);
    if (entries.length === 0) return false;

    pendingVideoEntriesRef.current.push(...entries);

    if (videoPayloadFlushRef.current === null) {
      videoPayloadFlushRef.current = requestAnimationFrame(() => {
        const pendingEntries = pendingVideoEntriesRef.current;
        pendingVideoEntriesRef.current = [];
        videoPayloadFlushRef.current = null;

        if (pendingEntries.length === 0) return;

        setMedia((current) => mergeVideoMedia(current, pendingEntries));
        setImagesData((current) => ({
          ...current,
          annotations: mergeVideoAnnotations(
            current?.annotations,
            pendingEntries
          ),
        }));
      });
    }

    setLoader(false);
    return true;
  }, []);

  const currentPageRef = useRef(null);
  const buildPredictionRequestPath = (pageNum) => {
    const [path, queryString = ""] = String(props.samUrl || "").split("?");
    const params = new URLSearchParams(queryString);
    const rawFilters = params.get("filters");
    let filters = props.id ? { _id: props.id } : {};

    if (rawFilters) {
      try {
        filters = JSON.parse(rawFilters);
      } catch (err) {
        try {
          filters = JSON.parse(decodeURIComponent(rawFilters));
        } catch (decodeErr) {
          const idMatch = String(rawFilters).match(/"_id"\s*:\s*"([^"]+)"/);
          filters = idMatch ? { _id: idMatch[1] } : filters;
        }
      }
    }

    delete filters[SEARCH_FILTER_KEYS.class];
    delete filters[SEARCH_FILTER_KEYS.image_name];
    params.set("section", "predictions");

    params.set("filters", JSON.stringify(filters));
    params.set("page", String(pageNum));
    params.set("page_size", String(PAGE_SIZE));

    return `${path}?${params.toString()}`;
  };

  useEffect(() => {
    currentPageRef.current = null;
    setPreviewSearchValue("");
  }, [props.id, props.samUrl, props.type]);

  /** Fetch a page of data with retry/backoff on CORS-like errors **/
  const fetchPage = async (
    pageNum,
    replace = false,
    attempt = 0,
    { force = false, silent = false } = {}
  ) => {
    if (props.type === 'video') {
      setLoader(true);
      const token = getCokkie("jwtToken");
      const endpoint = `${window["ENV"].API_URL}/${props.samUrl}`;
      videoEventSourceRef.current?.close();

      const es = new EventSourcePolyfill(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        heartbeatTimeout: 500000000000000,
      });
      videoEventSourceRef.current = es;

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (applyVideoPayload(data)) return;


          Object.keys(data).forEach((key) => {
            if (key.startsWith("frame_") || key.endsWith(".jpg")) {
              const frame = data[key];
              const { annotation, image_url, media_name, media_type, score, labels } = frame;

              const now = performance.now();

              // if (!firstPredictionTime) {
              //   firstPredictionTime = now;
              //   console.log("First prediction frame received at:", new Date().toLocaleTimeString());
              // } else {
              //   const delta = now - prevPredictionTime;
              //   console.log(`Time since last prediction: ${(delta / 1000).toFixed(2)} sec`);
              // }

              // prevPredictionTime = now;
              // lastPredictionTime = now;

              // ---- existing setMedia logic ----
              setMedia((prev = {}) => {
                let updated = { ...prev };

                if (media_type === "video") {
                  // ✅ stable video id from image_url
                  const videoId =
                    media_name || "video";

                  const existing = updated[videoId] || {
                    type: "video",
                    url: image_url,
                    frames: {},              // frameNo -> boxes[]
                    landmarksFrames: {},     // frameNo -> landmarks[]
                    prediction_interval: frame?.prediction_interval,
                  };

                  let updatedFrames = { ...existing.frames };
                  let updatedLandmarksFrames = { ...existing.landmarksFrames };

                  // ✅ robust frame number extraction:
                  // works for "frame_0231.jpg" AND "retinaface_frame_17.jpg"
                  const match = String(key).match(/(\d+)(?=\.jpg$)/);
                  const frameIndex = match ? parseInt(match[1], 10) : null;

                  // ---- Boxes ----
                  let boxes = [];
                  if (Array.isArray(annotation) && annotation.length > 0) {
                    boxes = (annotation?.[0] || []).map((ann, idx) => ({
                      x: ann[0],
                      y: ann[1],
                      w: ann[2],
                      h: ann[3],
                      label: `${ann[4]} (${(score?.[idx] ?? 0).toFixed(2)})`,
                      color: ann[5] || "lime",
                    }));
                  }

                  // ---- Landmarks ----
                  // Retinaface example: landmarks: [[[[x,y],[x,y]...]]]]
                  // Normalize to [[x,y],[x,y],...]
                  const lmRaw = frame?.landmarks;
                  const landmarks =
                    Array.isArray(lmRaw)
                      ? (lmRaw?.[0]?.[0] || lmRaw?.[0] || [])
                      : [];

                  // ✅ Apply across prediction_interval range if we have a frame index
                  // otherwise just store once using key as fallback (rare)
                  if (frameIndex != null) {
                    const interval = frame.prediction_interval || 30;
                    const start = (frameIndex - 1) * interval + 1;
                    const end = frameIndex * interval;

                    for (let i = start; i <= end; i++) {
                      if (boxes.length) updatedFrames[i] = boxes;
                      if (landmarks.length) updatedLandmarksFrames[i] = landmarks;
                    }
                  } else {
                    // fallback if frame number couldn't be parsed
                    if (boxes.length) updatedFrames[key] = boxes;
                    if (landmarks.length) updatedLandmarksFrames[key] = landmarks;
                  }

                  updated[videoId] = {
                    ...existing,
                    url: existing.url || image_url,
                    frames: updatedFrames,
                    landmarksFrames: updatedLandmarksFrames,
                    video_fps: frame?.video_fps,
                    prediction_interval: frame?.prediction_interval ?? existing.prediction_interval,
                    media_name: frame?.media_name
                  };
                }

                if (media_type === "image") {
                  const boxes = annotation[0].map((ann, idx) => ({
                    x: ann[0],
                    y: ann[1],
                    w: ann[2],
                    h: ann[3],
                    label: `${ann[4]} (${(score?.[idx] ?? 0).toFixed(2)})`,
                    color: ann[5] || "lime",
                  }));

                  updated[media_name] = {
                    type: "image",
                    url: image_url,
                    boxes,
                  };
                }

                return updated;
              });

              setImagesData(prev => {
                const isVideo = media_name.endsWith(".mp4");
                const key = isVideo ? `${media_name}-${frame.image_name}` : media_name;

                return {
                  ...prev,
                  annotations: {
                    ...prev.annotations,
                    [key]: {
                      ...frame,
                      media_type: isVideo ? "video" : "image",
                      media_name: media_name,
                    }
                  }
                };
              });

            }
          });

        } catch (err) {
          console.error("Stream parse error:", err);
        }
      };

      es.onerror = (err) => {
        console.error("EventSource failed:", err);
        es.close();
        if (videoEventSourceRef.current === es) {
          videoEventSourceRef.current = null;
        }
      };

      return () => {
        if (es) es.close();
        if (videoEventSourceRef.current === es) {
          videoEventSourceRef.current = null;
        }
      };
    } else {
      try {
        const requestPath = buildPredictionRequestPath(pageNum);

        if (!force && currentPageRef.current === requestPath) {
          if (replace) setLoader(false);
          setIsLoadingNextPage(false);
          return;
        }

        if (!silent) {
          if (replace) {
            setLoader(true);
          } else {
            setIsLoadingNextPage(true);
          }
        }

        currentPageRef.current = requestPath;

        const result = await getDataFromApi(
          `${window.ENV.API_URL}/${requestPath}`,
          "get"
        );

        const responseRoot = result?.[0] ?? result ?? {};
        const formData = responseRoot?.form_data;
        const responsePayload = {
          ...responseRoot,
          ...(formData && typeof formData === "object" ? formData : {}),
          annotations:
            formData?.annotations ??
            responseRoot?.annotations ??
            {},
          active_page:
            formData?.active_page ??
            responseRoot?.active_page,
          page_count:
            formData?.page_count ??
            responseRoot?.page_count,
          total_pages:
            formData?.total_pages ??
            responseRoot?.total_pages,
        };

        // console.log('data', result)
        setImagesData((prev) => ({
          ...prev,
          annotations: replace
            ? responsePayload.annotations || {}
            : { ...prev.annotations, ...(responsePayload.annotations || {}) },
        }));
        setPage(responsePayload.active_page ?? pageNum);
        setActivePage(responsePayload.active_page ?? pageNum);
        const responsePageCount =
          responsePayload.page_count ??
          responsePayload.total_pages ??
          result?.page_count ??
          result?.total_pages ??
          1;
        const processedImageCount = Number(props.completedCount || 0);
        const pausedPageCount = Math.max(
          1,
          Math.ceil(processedImageCount / PAGE_SIZE)
        );

        setTotalPages(
          normalizedEvaluationStatus === "paused" && processedImageCount > 0
            ? Math.min(Number(responsePageCount) || 1, pausedPageCount)
            : responsePageCount
        );

        if (!silent) {
          setLoader(false);
          setIsLoadingNextPage(false);
        }
      } catch (err) {
        if (isCorsLikeError(err) && attempt < MAX_RETRIES) {
          const delay = 500 * Math.pow(2, attempt); // 500ms, 1s, 2s
          setTimeout(
            () => fetchPage(pageNum, replace, attempt + 1, { force, silent }),
            delay
          );
          return; // keep loaders during retry
        }

        console.error("Fetch error:", err);
        if (!silent) {
          if (replace) setLoader(false);
          setIsLoadingNextPage(false);
        }
      }
    }
  };

  useEffect(() => {
    const llmReportAvailable = props.llmReportAvailable === true;
    const becameAvailable =
      llmReportAvailable && !previousLlmReportAvailableRef.current;

    previousLlmReportAvailableRef.current = llmReportAvailable;

    if (!becameAvailable || !props.id || props.type === "video") return;

    // AI reasoning is appended after evaluation predictions have streamed.
    // Refresh only the page the user is currently viewing and merge it into
    // the existing data so the preview and open Detail View remain mounted.
    fetchPage(activePage || page || 1, false, 0, {
      force: true,
      silent: true,
    });
    // fetchPage intentionally stays out of the dependency list: this effect
    // is driven only by the backend llm_report transition for this evaluation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.llmReportAvailable, props.id, props.type]);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return [h, m, s]
      .map((v) => String(v).padStart(2, "0"))
      .join(":");
  };

  const stopWorker = () => {
    if (videoPayloadFlushRef.current !== null) {
      cancelAnimationFrame(videoPayloadFlushRef.current);
      videoPayloadFlushRef.current = null;
    }
    pendingVideoEntriesRef.current = [];

    if (videoEventSourceRef.current) {
      videoEventSourceRef.current.close();
      videoEventSourceRef.current = null;
    }

    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
      hasStartedWorker.current = false;
    }
  };

  const buildPaginationItems = (current, max) => {
    if (max <= 12) return Array.from({ length: max }, (_, i) => i + 1);

    const items = new Set();

    // first block
    [1, 2, 3, 4].forEach((p) => items.add(p));

    // last block
    [max - 1, max].forEach((p) => items.add(p));

    // around current
    for (let p = current - 2; p <= current + 2; p++) {
      if (p >= 1 && p <= max) items.add(p);
    }

    const sorted = Array.from(items).sort((a, b) => a - b);

    const out = [];
    for (let i = 0; i < sorted.length; i++) {
      out.push(sorted[i]);
      if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) out.push("…");
    }
    return out;
  };

  const normalizedEvaluationStatus = String(props.message || "").toLowerCase();
  const isEvaluating = normalizedEvaluationStatus === "evaluating";
  const isStaticEvaluation = ["complete", "terminated", "paused"].includes(
    normalizedEvaluationStatus
  );
  const isComplete = isStaticEvaluation;
  const hasMorePages = isEvaluating ? activePage < availablePages : activePage < totalPages;

  const loadPage = useCallback(
    (pageNum) => {
      // console.log("pageNum", pageNum);
      const requested = Math.max(1, Number(pageNum) || 1);

      // clamp max based on mode
      const maxAllowed = isEvaluating ? availablePages : totalPages;
      const p = Math.min(requested, Math.max(1, maxAllowed));



      // sync UI
      setActivePage(p);
      setPage(p);
      setPageInput(String(p));
      setSliderIndex(0);

      // stop any active stream before switching pages
      stopWorker();

      // reset display data for clean rendering of selected page
      setImages([]);
      setImagesData({ annotations: {} });
      setParentData({ annotations: {} });
      setMedia({});
      setLoader(true);

      if (props.type === "video") {
        // video path uses SSE/fetchPage already
        fetchPage(p, true);
        return;
      }

      if (isEvaluating) {
        startWorker(p);   // ✅ streaming results
      } else {
        fetchPage(p, true); // ✅ completed results via JSON
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEvaluating, availablePages, totalPages, props.type, fetchPage]
  );

  /** Initial fetch or SSE streaming **/
  useEffect(() => {
    if (!props.samUrl || !props.id) return;

    if (props.message === "Failed") return;

    if (isStaticEvaluation) {

      if (window._ora) {
        window._ora.push([
          "trackEvent",
          {
            type: "card_click",
            name: "Archived Evaluation",
            component: "Evaluation Page",
          }
        ]);
      }

      // console.log("message", props.message);

      // if(props.predictioncallstatus) {
      // fetchPage(1, true);
      // setLoader(true);
      // setImages([]);
      // return;
      loadPage(1);
      return;
      // }
    }

    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
      hasStartedWorker.current = false;
    }

    // Reset relevant state for new evaluation
    setImages([]);
    setImagesData({ annotations: [] });
    setMedia({});
    setLoader(true);

    // --- Handle converting state (SSE progress + elapsed time) ---
    if (props.message === "Converting") {
      setIsConverting(true);
      const id = props.id;
      const token = getCokkie("jwtToken");
      const endpoint = `${window.ENV.API_URL}/evaluations?filters={"_id":"${id}"}&section=conversion_progress`;

      // Record start time if not already present
      setStartTimeMap((prev) => {
        if (!prev[id]) {
          return { ...prev, [id]: Date.now() };
        }
        return prev;
      });

      // Start timer if not already running
      if (!timerRefs.current[id]) {
        timerRefs.current[id] = setInterval(() => {
          setStartTimeMap((prevMap) => {
            const start = prevMap[id];
            if (start) {
              const seconds = Math.floor((Date.now() - start) / 1000);
              setElapsedTime(formatTime(seconds));
            }
            return prevMap;
          });
        }, 1000);
      }

      // --- SSE (progress only, ignore elapsed_time from backend) ---
      const es = new EventSourcePolyfill(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      es.onmessage = (event) => {
        try {
          const cleaned = event.data.replace(/'/g, '"');
          const parsed = JSON.parse(cleaned);

          if (parsed.progress) {
            const progressValue = parseInt(parsed.progress.replace("%", ""), 10);
            setConversionProgress(progressValue);
          }
        } catch (err) {
          console.error("Error parsing SSE data:", err, event.data);
        }
      };

      es.onerror = (error) => {
        console.error("SSE connection error:", error);
        es.close();
        setIsConverting(false);
      };

      // Cleanup
      return () => {
        es.close();
        if (timerRefs.current[id]) {
          clearInterval(timerRefs.current[id]);
          delete timerRefs.current[id];
        }
        setIsConverting(false);
      };
    }

    if (
      props.message !== "Starting" &&
      props.message !== "Complete" &&
      props.message &&
      !hasStartedWorker.current
    ) {
      loadPage(1);

    }

    return () => {
      if (videoPayloadFlushRef.current !== null) {
        cancelAnimationFrame(videoPayloadFlushRef.current);
        videoPayloadFlushRef.current = null;
      }
      pendingVideoEntriesRef.current = [];

      if (videoEventSourceRef.current) {
        videoEventSourceRef.current.close();
        videoEventSourceRef.current = null;
      }

      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
        hasStartedWorker.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id, props.samUrl, props.type]);

  useEffect(() => {
    if (props.id && startTimeMap[props.id]) {
      const start = startTimeMap[props.id];
      const seconds = Math.floor((Date.now() - start) / 1000);
      setElapsedTime(formatTime(seconds));
    } else {
      setElapsedTime("");
    }
  }, [props.id, startTimeMap]);

  useEffect(() => {
    if (props.message !== "Evaluating" && props.message !== "Paused") return;

    const completed = Number(props.completedCount || 0);
    const unlocked = Math.max(1, Math.ceil(completed / PAGE_SIZE));
    setAvailablePages(unlocked);

    if (props.message === "Paused" && completed > 0) {
      setTotalPages(unlocked);
      setActivePage((currentPage) => Math.min(currentPage, unlocked));
      setPageInput((currentPageInput) => {
        const requestedPage = Number(currentPageInput || 1);
        return String(Math.min(requestedPage, unlocked));
      });
    }
  }, [props.message, props.completedCount]);

  const startWorker = (pageNum) => {
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
      hasStartedWorker.current = false;
    }

    const token = getCokkie("jwtToken");
    const worker = new Worker(sampleSSEWorker, { type: "module" });

    workerRef.current = worker;
    hasStartedWorker.current = true;

    worker.postMessage({
      url: `${window.ENV.API_URL}/${props.samUrl}&page=${pageNum}&page_size=${PAGE_SIZE}`,
      token,
    });

    worker.onmessage = (event) => {
      const { type, data, message, retries } = event.data;

      if (type === "retry") {
        console.warn(`SSE retry #${retries}:`, message);
        return;
      }

      if (type === "parse_error") {
        console.warn(message);
        return;
      }

      if (type === "message") {
        if (applyVideoPayload(data)) return;

        if (props.task_type === "FaceRecognition") {
          setImagesData((prev) => ({
            ...prev,
            annotations: { ...prev.annotations, ...data.annotations },
          }));
        } else {
          setImagesData((prev) => ({
            ...prev,
            annotations: { ...prev.annotations, ...data },
          }));
        }
        return;
      }

      if (type === "complete") {
        worker.terminate();
        workerRef.current = null;
        hasStartedWorker.current = false;
        return;
      }

      if (type === "fatal") {
        console.error("SSE fatal:", message);

        worker.terminate();
        workerRef.current = null;
        hasStartedWorker.current = false;

        if (isCorsLikeError(message)) {
          fetchPage(pageNum, true);
        }
      }
    };

    // Extra safety: cleanup if worker crashes
    worker.onerror = () => {
      worker.terminate();
      workerRef.current = null;
      hasStartedWorker.current = false;
    };
  };

  useEffect(() => {
    const previousStatus = previousEvaluationStatusRef.current;
    previousEvaluationStatusRef.current = normalizedEvaluationStatus;

    if (
      previousStatus === "evaluating" &&
      ["paused", "complete", "terminated"].includes(normalizedEvaluationStatus)
    ) {
      stopWorker();
      return;
    }

    if (
      normalizedEvaluationStatus === "evaluating" &&
      previousStatus !== "evaluating"
    ) {
      currentPageRef.current = null;
      startWorker(activePage || 1);
    }
    // Status transitions out of Evaluating intentionally retain streamed data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedEvaluationStatus]);


  /** Extract the images currently visible after applying the local search. **/
  useEffect(() => {
    const extractedImages = Object.values(displayedAnnotations)
      .map(getAnnotationImage)
      .filter(Boolean);

    setImages(extractedImages);
    setParentData({
      ...imagesData,
      annotations: displayedAnnotations,
    });

    if (extractedImages.length > 0 || displayedMediaEntries.length > 0) {
      setLoader(false);
    }
  }, [displayedAnnotations, displayedMediaEntries.length, imagesData]);




  useLayoutEffect(() => {
    if (props.message === "Evaluating") {
      const el = scrollContainerRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }
  }, [imagesData, props.message]);


  const imageData = imagesData?.annotations;
  const annotations = Object.entries(imageData)?.[0]?.[1]?.annotations || {};
  // console.log(Object.entries(imageData)?.[0]?.[1]?.annotations || {});
  const totalImages =
    imageData &&
    (Object.entries(imageData)?.[0]?.[1]?.total_images ||
      Object.entries(imageData)?.[0]?.[1]?.annotation?.total_images || Object.values(annotations)?.[0]?.total_images);
  const fetchedImages = imageData && Object.entries(imageData)?.length;



  useEffect(() => {
    if (!props?.onEvaluationCreated) return;

    props.onEvaluationCreated(totalImages, fetchedImages);

    if (props.message === "Evaluating") {
      setTimeout(() => {
        const el = scrollContainerRef.current;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      }, 0);
    }
  }, [totalImages, imageData]);

  useEffect(() => {
    if (props.message === "Complete" && props.total_pages) {
      setTotalPages(props.total_pages);
    }
  }, [props.message, props.total_pages]);


  const loaderMsgByStatus = {
    starting: `Model Loading on ${props?.targethw}....`,
    converting: 'Model Conversion is in Progress....',
    terminated: 'Evaluation Terminated....',
    paused: 'Evaluation Paused....',
    failed: 'Evaluation Failed....',
    evaluating: 'Evaluating....',
    complete: 'Complete....',
    queued: 'Evaluation Queued....'
  };

  //   const status = String(props?.message ?? '').toLowerCase();
  // const loaderMessage = loaderMsgByStatus[status] ?? 'Analysing..';

  useEffect(() => {
    const newStatus = String(props?.message ?? '').toLowerCase();
    setStatus(newStatus);

    const newLoaderMessage = loaderMsgByStatus[newStatus] ?? 'Analysing..';
    setLoaderMessage(newLoaderMessage);
  }, [props?.message]);

  // after imagesData updates
  useEffect(() => {
    const annotations = displayedAnnotations;
    const keys = Object.keys(annotations);

    // build mapping: media key → index in annotations
    const map = {};
    keys.forEach((key, i) => {
      map[key] = i;
    });

    setAnnotationIndexMap(map);
  }, [displayedAnnotations]);

  const maxPages = hasStreamedVideo
    ? Math.max(1, Math.ceil(streamedVideoCount / PAGE_SIZE))
    : isEvaluating
      ? Math.max(Number(totalPages) || 0, Number(availablePages) || 1)
      : (totalPages || availablePages);

  const totalCount =
    (hasStreamedVideo
      ? streamedVideoCount
      : totalImages || props.imageslength || images.length) || "";

  const startIndex =
    totalCount > 0 ? (activePage - 1) * PAGE_SIZE + 1 : "";

  const endIndex =
    totalCount > 0
      ? Math.min(activePage * PAGE_SIZE, totalCount)
      : "";

  const hasPreviewPagination = Boolean(maxPages) && maxPages > 1 && props?.message !== "Starting";
  const loaderMessageGapStyle = hasPreviewPagination ? { marginTop: "28px" } : undefined;
  const previewPaginationControls = (() => {
    if (maxPages <= 1) return null;
    if (!maxPages) return null;
    if (props?.message === "Starting") return null;

    const items = buildPaginationItems(activePage, Math.max(1, maxPages));

    return (
      <div className="prediction-pagination-controls sample-preview-pagination-controls">
        <div className="pagination-pages d-flex justify-content-center align-items-center gap-2" >
          {items.map((it, idx) => {
            if (it === "…") return <span key={`dots-${idx}`} className="px-1">…</span>;

            const pageNum = it;
            const isActive = pageNum === parseInt(activePage);

            return (
              <button
                key={`p-${pageNum}`}
                className={`pagination-btn text ${isActive ? "active" : ""}`}
                disabled={isEvaluating && pageNum > availablePages}
                onClick={() => {
                  if (pageNum === activePage) return;
                  loadPage(pageNum);
                }}
              >
                {pageNum}
              </button>
            );
          })}
        </div>

        <div className="pagination-go d-flex justify-content-end align-items-center gap-1">
          <input
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value.replace(/[^\d]/g, ""))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault(); // prevents form submit if inside form
                if (pageInput >= 1 && pageInput <= maxPages) {
                  if (pageInput) {
                    loadPage(Number(pageInput));
                  }

                }
              }
            }}
            className="page-input text"
            placeholder="Page"
            style={{ width: 25 }}
          />
          <button
            className="evaluation-results-link-btn"
            onClick={() => {
              if (pageInput >= 1 && pageInput <= maxPages) {
                loadPage(Number(pageInput));
              }
            }
            }
          >
            GO
          </button>
        </div>
      </div>
    );
  })();
  const previewPaginationFooter = previewPaginationControls && (
    <div className="prediction-pagination-footer sample-preview-pagination-footer">
      {previewPaginationControls}
    </div>
  );
  const useToolbarHeader = Boolean(props.compactHeader);
  const previewHeader = (
    <div
      className={`prediction-pagination-header sample-preview-header${
        useToolbarHeader ? " sample-preview-header--toolbar" : ""
      }${props.hideTitle ? " sample-preview-header--title-hidden" : ""}`}
    >
      {!props.hideTitle && (
        <div className="d-flex align-items-center gap-2 sample-preview-header__title">
          <ImagesIcon className="wf-icon" style={{ padding: "3px" }} />
          <span className="evaluation-results-section-title">Prediction Preview</span>
        </div>
      )}
      {props.message === "Complete" && (
        <div className="prediction-search-form sample-preview-search-form">
          <div className="d-a container-card px-3 py-1 rounded prediction-search-input bg-transparent">
            <FontAwesomeIcon
              className="plain-button2 text-secondary"
              icon={faSearch}
              style={{ position: "relative", opacity: "0.8" }}
            />
            <input
              type="text"
              className="search-1 bg-transparent"
              placeholder="Search by label or image name..."
              value={previewSearchValue}
              onChange={(event) => setPreviewSearchValue(event.target.value)}
              aria-label="Search prediction preview by label or image name"
            />
            {previewSearchValue && (
              <button
                type="button"
                className="plain-button2 text-secondary sample-preview-search-clear"
                onClick={() => setPreviewSearchValue("")}
                aria-label="Clear search"
              >
                <FontAwesomeIcon icon={faClose} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
  const predictionDetailOverlay = predictions && typeof document !== "undefined"
    ? createPortal(
      <div
        className="sample-bg evaluation-prediction-detail-overlay"
        onClick={() => setPredictions(false)}
      >
        <div
          className="modal-905 w-100 mx-0 mb-0"
          onClick={(event) => event.stopPropagation()}
        >
          <Prediction
            key={`${props.id || props.samUrl || "prediction"}-${activePage}`}
            type={props.type}
            id={props.id}
            query={props.query}
            visibleCanvas
            takeCount
            samUrl={props.samUrl}
            modelName={props.modelName}
            tasktype={props.task_type}
            ParentData={ParentData}
            takeParent
            setParentData={setParentData}
            sliderIndex={sliderIndex}
            setSliderIndex={setSliderIndex}
            setPredictions={setPredictions}
            message={props.message}
            totalImages={totalImages}
            videoFrames={media}
            fetchedImages={Object.entries(media)?.length}
            initialPage={activePage}
            initialTotalPages={maxPages}
            pageSize={PAGE_SIZE}
            loadDetailPage={async (nextPage) => {
              if (isEvaluating) {
                loadPage(nextPage);
                return null;
              }

              const result = await getDataFromApi(
                `${window.ENV.API_URL}/${buildPredictionRequestPath(nextPage)}`,
                "get"
              );
              const responseRoot = result?.[0] ?? result ?? {};
              const formData = responseRoot?.form_data;

              return {
                ...responseRoot,
                ...(formData && typeof formData === "object" ? formData : {}),
                annotations:
                  formData?.annotations ?? responseRoot?.annotations ?? {},
                active_page:
                  formData?.active_page ?? responseRoot?.active_page ?? nextPage,
                page_count:
                  formData?.page_count ??
                  responseRoot?.page_count ??
                  responseRoot?.total_pages ??
                  totalPages,
              };
            }}
            onDetailPageSync={({ page: nextPage, data }) => {
              const resolvedPage = Number(nextPage) || activePage;
              const resolvedTotalPages = Number(
                data?.page_count ?? data?.total_pages ?? totalPages
              ) || 1;

              setPage(resolvedPage);
              setActivePage(resolvedPage);
              setTotalPages(resolvedTotalPages);
              setImagesData((current) => ({
                ...current,
                ...data,
                annotations: data?.annotations || {},
              }));
              setParentData(data);
              setSliderIndex(0);
            }}
          />
        </div>
      </div>,
      document.body
    )
    : null;

  return (
    <div className={`sample-preview-layout${useToolbarHeader ? " sample-preview-layout--embedded" : ""}`}>
      {previewHeader}

      {hasPreviewContent ? (
        <>
          <div
            className="sample-preview-media-scroll"
            ref={scrollContainerRef}
            style={{
              overflowY: "auto",
              marginTop: maxPages > 1 ? "" : "",
              // paddingRight: "8px",""
            }}
          // onScroll={props.message === "Complete" ? handleContainerScroll : undefined}
          >
            {/* Face Recognition references */}
            <div
              className="grid-container mt-2 sample-preview-media-grid"
            >
              <div
                className="sample-preview-media-row"
                style={{
                  display: "grid",
                  gridTemplateColumns: getPreviewGridTemplateColumns(),
                  gap: "10px",
                  width: "100%",
                }}
              >
                {displayedMediaEntries.map(([name, item]) => {
                  const matchingKey = Object.keys(annotationIndexMap).find(k =>
                    k.startsWith(name)
                  );

                  const correctIndex = annotationIndexMap[matchingKey];
                  return (
                    <button
                      key={name}
                      className={`plain-button2 prediction-image  ${item.type === "video" ? ' media-button' : 'image-button'}`}
                      onClick={() => {
                        setPredictions(true);
                        setSliderIndex(correctIndex);
                      }}
                      style={{
                        marginBottom: 0,
                        display: "flex",
                        flex: "none",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "relative",
                        overflow: "hidden",
                        width: "100%",
                        height: "100px",
                        minWidth: 0,
                        maxWidth: "none",
                        maxHeight: "100px",
                      }}
                    >
                      {item.type === "video" && !predictions && (
                        <EvaluationVideo
                          url={item.url}
                          frames={item.frames}
                          predictionInterval={item.prediction_interval}
                          video_fps={item.video_fps}
                          playMode="hover"
                          labelvisible={true}
                          landmarks={item?.landmarksFrames}
                        />
                      )}


                      {item.type === "image" && (
                        <img
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "center",
                          }}
                          src={getPreviewImageSrc(item.url)}
                          alt={name}
                        />
                      )}

                      <span
                        className="rounded"
                        style={{
                          position: "absolute",
                          bottom: 0,
                          width: "93%",
                          backgroundColor: "rgba(0, 0, 0, 0.2)",
                          color: "whitesmoke",
                          textAlign: "center",
                        }}
                      >
                        {name}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
            <div
              className="grid-container pe-2 sample-preview-grid"
              style={{
                display: "grid",
                gridTemplateColumns: getPreviewGridTemplateColumns(),
                gap: "10px",
                maxHeight: "460px",
                overflowY: "auto",
                scrollbarWidth: 'thin',
                width: '99.5%'
              }}
            >
              {Object.entries(displayedAnnotations).map(
                ([referenceImageName, data], index) => {
                  const faceImage = getAnnotationImage(data);
                  const isAlreadyRendered = displayedMediaEntries.some(
                    ([mediaName, mediaItem]) =>
                      mediaName === referenceImageName ||
                      (data?.media_name && mediaName === data.media_name) ||
                      (faceImage && mediaItem?.url === faceImage)
                  );

                  return (
                    isFaceRecognitionType(data?.type || data?.task_type || data?.taskType) &&
                    faceImage &&
                    displayedMediaEntries.length === 0 &&
                    !isAlreadyRendered && (

                      <button
                        key={`face-${referenceImageName}`}
                        className="plain-button2 p-0 rounded w-100 sample-preview-face-tile"
                        onClick={() => {
                          setPredictions(true);
                          setSliderIndex(index);
                        }}
                      >
                        <img
                          src={getPreviewImageSrc(faceImage)}
                          alt={referenceImageName}
                          loading="lazy"
                          decoding="async"
                        />
                      </button>

                    )
                  );
                }
              )}

            </div>

            <div
              className="grid-container pe-2 sample-preview-grid"
              style={{
                display: "grid",
                gridTemplateColumns: getPreviewGridTemplateColumns(),
                gap: "10px",
                maxHeight: "460px",
                overflowY: "auto",
                scrollbarWidth: 'thin',
                width: '99.5%'
              }}
            >
              {filteredImages.map((item, index) => (
                <button
                  key={index}
                  className="plain-button2 p-0 rounded w-100"
                  style={{ height: "100px" }}
                  onClick={() => {
                    setPredictions(true);
                    setSliderIndex(index);
                  }}
                >
                  <ImageCanvas
                    img={{
                      url: getPreviewImageSrc(getAnnotationImage(item)),
                      boxes: props.task_type !== "Pose Estimation" ? item?.annotation?.[0] : "",
                      landmarks: item?.landmarks,
                    }}
                  />
                </button>
              ))}

            </div>
            {isLoadingNextPage && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "10px" }}>
                <Loader />
              </div>
            )}

          </div>

          {previewPaginationFooter}

        </>
      ) : loader ? (
        (props.message === "Evaluating" || props.message === "Starting") ? (
          <>
            {props.message === "Evaluating" && evaluatingStep === 3 ? (
              <>
                <div className="pt-4" style={{ display: "flex", flexWrap: "wrap", gap: "1%" }}>
                  {[...Array(PAGE_SIZE)].map((_, index) => (
                    <Skeleton
                      key={index}
                      variant="rounded"
                      height={110}
                      style={{ minWidth: '13.3%', maxWidth: '130px'}}
                    />
                  ))}
                </div>
                {previewPaginationFooter}
              </>
            ) :
              <div className="d-j-a flex-column h-100 text-center">
                <div className="queue-icon-status-wrapper mb-3">
                  <QueueChipIcon className="wf-icon-blue" />
                  <span className="queue-status-dot" />
                </div>

                <h6 className="mb-2 text-uppercase">
                  {props.message === "Starting" &&
                    `Model Loading on ${props?.targethw}`}
                  {props.message === "Evaluating" && evaluatingStep === 0 &&
                    `Model Loading on ${props?.targethw}`}
                  {props.message === "Evaluating" && (evaluatingStep === 1 || evaluatingStep === 2) &&
                    `Evaluation in Progress on ${props?.targethw}`}
                </h6>

                <p className="text-secondary mb-3">
                  {props.message === "Starting" && (
                    <>
                      Job <strong className="text-capitalize">{props.modelName || "Job"}</strong> is preparing to run.
                      <br />
                      The model is being loaded on{" "}
                      <strong className="text-uppercase">
                        {props.targethw || "N1"}
                      </strong>{" "}
                      device.
                    </>
                  )}

                  {props.message === "Evaluating" && evaluatingStep === 0 && (
                    <>
                      Job <strong className="text-capitalize">{props.modelName || "Job"}</strong> is preparing to run.
                      <br />
                      The model is being loaded on{" "}
                      <strong className="text-uppercase">
                        {props.targethw || "N1"}
                      </strong>{" "}
                      device.
                    </>
                  )}

                  {props.message === "Evaluating" && evaluatingStep === 1 && (
                    <>
                      Job <strong className="text-capitalize">{props.modelName || "Job"}</strong> is currently running.
                      <br />
                      The model is actively evaluating on{" "}
                      <strong className="text-uppercase">
                        {props.targethw || "N1"}
                      </strong>{" "}
                      device.
                    </>
                  )}

                  {props.message === "Evaluating" && evaluatingStep === 2 && (
                    <p className="text-secondary mt-2" style={{ fontSize: "13px" }}>
                      Almost done — finalizing results. You’ll see them shortly.
                    </p>
                  )}

                </p>


                {/* {props.message === "Starting" &&
                  <p className="text-secondary mb-3">
                    This step may take a few moments. Execution will begin automatically once setup is complete.</p>} */}

                {/* {props.message === "Evaluating" && evaluatingStep === 0 &&
                  <p className="text-secondary mb-3">
                    Results will be available once the evaluation is complete.</p>} */}

              </div>}
          </>
        ) : props.message === "Complete" ? (
          <>
            <div className="pt-4" style={{ display: "flex", flexWrap: "wrap", gap: "1%" }}>
              {[...Array(PAGE_SIZE)].map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  height={110}
                  style={{ minWidth: "13.3%", maxWidth: "130px", marginBottom: "10px" }}
                />
              ))}
            </div>
            {previewPaginationFooter}
          </>
        ) : (
          <>
            <div >
              <Loader message={loaderMessage} />
            </div>
            {previewPaginationFooter}
            {loaderMessage === 'Model Conversion is in Progress....' && isConverting && (
              <div style={{ width: "80%", margin: "20px auto" }}>
                <div style={{ height: "8px", backgroundColor: "#e0e0e0", borderRadius: "4px" }}>
                  <div
                    style={{
                      width: `${conversionProgress}%`,
                      backgroundColor: "var(--leading-color)",
                      height: "100%",
                      borderRadius: "4px",
                      transition: "width 0.3s ease-in-out",
                    }}
                  />
                </div>
                <p style={{ textAlign: "center", marginTop: "8px", color: "#555" }}>
                  {conversionProgress}%
                </p>
                {elapsedTime && (
                  <p style={{ color: "#777", fontSize: "14px", marginTop: "4px" }}>
                    ⏱ Elapsed Time: {elapsedTime}
                  </p>
                )}
              </div>
            )}
          </>
        ))
        : images.length === 0 && props.message === "Terminated" ? (
          <>
            <div style={loaderMessageGapStyle}>
              <Loader message={loaderMessage} />
            </div>
            {previewPaginationFooter}
          </>

        ) : (
          <>
            <ErrorScreen sub={"Oops! No predictions found"} />
            {previewPaginationFooter}
          </>
        )}

      <ModalComponent
        modalIsOpen={modalIsOpen}
        setModalIsOpen={setModalIsOpen}
        component={Component}
        modalClass={"modal-90"}
      />
      {predictionDetailOverlay}
    </div>
  );
};

export default Samplepredictionstream;
